<script lang="ts">
  /**
   * Squad review, and the one piece of user input in the whole draft.
   *
   * The name field comes first. Asking for it below a thirteen-row list means
   * scrolling back up to find what the disabled button was waiting on.
   */
  import type { Draft } from '../lib/draft.svelte';
  import { roleLabel } from '../lib/format';

  interface Props {
    draft: Draft;
    onback: () => void;
    onnext: () => void;
    /** Overridden in a 1v1 room, where the next step is locking the squad in. */
    nextLabel?: string;
  }
  let { draft, onback, onnext, nextLabel }: Props = $props();

  const named = $derived(draft.teamName.trim().length > 0);
</script>

<section class="screen active" aria-label="Your squad">
  <div class="body">
    <div class="field">
      <label for="teamName">Name your team</label>
      <input
        id="teamName"
        placeholder="e.g. Gully Legends"
        autocomplete="off"
        maxlength="24"
        bind:value={draft.teamName}
      />
      <p class="hint">
        {named ? `Your side: ${draft.teamName.trim()}` : 'Required — this is what your opponent sees.'}
      </p>
    </div>

    <p class="eyebrow">Starting XI</p>
    <ol class="xi" style="max-height:none">
      {#each draft.xi as row (row.slot)}
        <li class:filled={row.player} data-role={row.player?.role}>
          <span class="s">{row.slot}</span>
          {#if row.player}
            <span class="who">
              <span class="nmtxt">{row.player.name}</span>
              {#if !row.player.isIndian}
                <svg class="plane" aria-hidden="true"><use href="#i-plane" /></svg>
                <span class="sr">Overseas</span>
              {/if}
              <span class="from">{row.player.from}</span>
            </span>
            <span class="mini">{roleLabel(row.player.role)}</span>
          {:else}
            <span class="empty">—</span>
            <span></span>
          {/if}
        </li>
      {/each}
    </ol>

    <p class="eyebrow" style="margin-top:var(--space-4)">Impact subs</p>
    <ul class="subs">
      {#each draft.subs as sub, i (i)}
        {#if sub}
          <li data-role={sub.role}>
            <span class="who">{sub.name}</span>
            <span class="from">{sub.from}</span>
            <span class="mini tagrole">{roleLabel(sub.role)}</span>
          </li>
        {:else}
          <li class="empty"><span>Not chosen yet</span></li>
        {/if}
      {/each}
    </ul>
    <p class="faint" style="margin-top:var(--space-2)">
      One of the two may come on during the match.
    </p>

    <!--
      The engine's own verdict on the finished squad, not a second opinion
      computed here. Five bowlers is exactly twenty overs with no slack, which
      is legal and worth saying out loud before the toss rather than at the
      death of an innings that cannot be bowled.
    -->
    {#each draft.validation.warnings as warning (warning)}
      <p class="faint" style="margin-top:var(--space-3);color:var(--danger)">{warning}</p>
    {/each}
  </div>

  <div class="actions">
    <button class="btn-primary" disabled={!named} onclick={onnext}>{nextLabel ?? 'Go to toss'}</button>
    <button class="btn-ghost" onclick={onback}>Back to draft</button>
  </div>
</section>
